(function() {
    class Categories {
        selectors = {
            categoryButton: '.js-info-section-category-button',
            categoryContainer: '.js-info-section-category-container',
            navigation: '.info-section-navigation',
            content: '.info-section__content'
        };

        classes = {
            active: 'active'
        };

        constructor(section) {
            this.section = section;
            this.categoryButtons = [...section.querySelectorAll(this.selectors.categoryButton)];
            this.categoryContainers = [...section.querySelectorAll(this.selectors.categoryContainer)];
            this.allButton = this.categoryButtons.find(button => !button.dataset.target);

            if(!this.categoryButtons.length || !this.categoryContainers.length) return;

            this.init();
        }

        init() {
            this.categoryButtons.forEach(categoryButton => {
                categoryButton.addEventListener('click', () => {
                    this.changeCategory(categoryButton);
                });
            });
        }

        changeCategory(categoryButton) {
            if (categoryButton.classList.contains(this.classes.active)) return;

            const target = categoryButton.dataset.target;
            this.setActiveButton(categoryButton);
            target ? this.showCategory(target) : this.showAllCategories();
            this.scrollNavToButton(categoryButton);
            this.scrollToContent();
        }

        // on the mobile scroll row a tapped edge chip can sit half (or fully)
        // outside the viewport; center it without moving the page vertically
        scrollNavToButton(categoryButton) {
            const nav = this.section.querySelector(this.selectors.navigation);

            if (!nav || nav.scrollWidth <= nav.clientWidth) return;

            const buttonRect = categoryButton.getBoundingClientRect();
            const navRect = nav.getBoundingClientRect();

            if (buttonRect.left >= navRect.left && buttonRect.right <= navRect.right) return;

            const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
            const target = nav.scrollLeft + (buttonRect.left - navRect.left) - (nav.clientWidth - buttonRect.width) / 2;
            nav.scrollTo({ left: target, behavior });
        }

        // switching to a shorter category from deep in the page collapses
        // the page height under the scroll position and strands the viewport
        // at the footer; re-anchor the content just below the pinned stack
        scrollToContent() {
            const content = this.section.querySelector(this.selectors.content);

            if (!content) return;

            const pinned = parseFloat(getComputedStyle(this.section).getPropertyValue('--info-rail-top')) || 120;
            const targetTop = Math.max(content.getBoundingClientRect().top + window.scrollY - pinned - 16, 0);

            if (window.scrollY <= targetTop) return;

            const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
            window.scrollTo({ top: targetTop, behavior });
        }

        // active state is aria-current, not disabled: a disabled button
        // drops out of the tab order and reads as unavailable
        setActiveButton(categoryButton) {
            this.categoryButtons.forEach(button => {
                button.classList.remove(this.classes.active);
                button.removeAttribute('aria-current');
            });

            categoryButton.classList.add(this.classes.active);
            categoryButton.setAttribute('aria-current', 'true');
        }

        showCategory(targetId) {
            this.categoryContainers.forEach(categoryContainer => {
                if (categoryContainer.id === targetId) {
                    categoryContainer.classList.add(this.classes.active);
                } else {
                    categoryContainer.classList.remove(this.classes.active);
                }
            });
        }

        showAllCategories() {
            this.categoryContainers.forEach(categoryContainer => {
                categoryContainer.classList.add(this.classes.active);
            });
        }

        // search spans every category, with or without a dedicated all button
        activateAll() {
            this.showAllCategories();

            if (this.allButton && !this.allButton.classList.contains(this.classes.active)) {
                this.setActiveButton(this.allButton);
            }
        }

        // undo activateAll once search clears: show only the selected category
        restoreActiveCategory() {
            const activeButton = this.categoryButtons.find(button => button.classList.contains(this.classes.active));

            if (!activeButton) return;

            const target = activeButton.dataset.target;
            target ? this.showCategory(target) : this.showAllCategories();
        }
    }

    class Accordions {
        selectors = {
            accordionItem: '.js-info-section-accordion-item',
            accordionControl: '.info-section-accordion-item__control',
            accordionContent: '.js-info-section-accordion-content',
            accordionContentInner: '.js-info-section-accordion-content-inner',
            deferredIframe: '.js-info-section-deferred-iframe'
        };

        classes = {
            active: 'active'
        };

        constructor(section) {
            this.section = section;
            this.accordions = [];
            this.init();
        }

        init() {
            const accordionElements = this.section.querySelectorAll(this.selectors.accordionItem);

            accordionElements.forEach(element => {
                this.setupAccordion(element);
            });
        }

        setupAccordion(element) {
            const control = element.querySelector(this.selectors.accordionControl);
            const content = element.querySelector(this.selectors.accordionContent);
            const contentInner = element.querySelector(this.selectors.accordionContentInner);

            if (!content || !contentInner) return;

            const accordionData = {
                element,
                control,
                content,
                contentInner,
                isOpen: element.dataset.accordionOpen === 'true',
                timeout: null
            };

            if (control) {
                if (!content.id) content.id = `info-section-accordion-content-${this.accordions.length}`;
                control.setAttribute('aria-controls', content.id);
                control.setAttribute('aria-expanded', String(accordionData.isOpen));
            }

            this.accordions.push(accordionData);

            // don't collapse when clicking inside the open answer (links, text selection)
            element.addEventListener('click', (event) => {
                if (content.contains(event.target)) return;
                this.toggle(accordionData);
            });

            if (accordionData.isOpen) {
                this.hydrateDeferredIframes(accordionData);
                accordionData.element.classList.add(this.classes.active);
                accordionData.content.style.height = 'auto';
            }
        }

        // iframes (e.g. youtube embeds) sit in <template> wrappers so the
        // browser doesn't fetch the embed bundle until the user expands the
        // accordion. <template> content is parsed but not in the live DOM.
        hydrateDeferredIframes(accordion) {
            const wrappers = accordion.element.querySelectorAll(this.selectors.deferredIframe);
            wrappers.forEach(wrapper => {
                const template = wrapper.querySelector('template');
                if (!template) return;
                wrapper.appendChild(template.content.cloneNode(true));
                template.remove();
            });
        }

        toggle(accordion) {
            accordion.isOpen ? this.close(accordion) : this.open(accordion);
        }

        open(accordion) {
            if (accordion.isOpen) return;

            this.hydrateDeferredIframes(accordion);
            clearTimeout(accordion.timeout);
            accordion.isOpen = true;
            accordion.control?.setAttribute('aria-expanded', 'true');
            accordion.element.classList.add(this.classes.active);
            accordion.content.style.height = `${accordion.contentInner.offsetHeight}px`;

            accordion.timeout = setTimeout(() => {
                accordion.content.style.height = 'auto';
            }, 300);
        }

        close(accordion) {
            if (!accordion.isOpen) return;

            clearTimeout(accordion.timeout);
            accordion.isOpen = false;
            accordion.control?.setAttribute('aria-expanded', 'false');
            accordion.element.classList.remove(this.classes.active);
            accordion.content.style.height = `${accordion.contentInner.offsetHeight}px`;
            accordion.timeout = setTimeout(() => {
                accordion.content.style.height = 0;
            }, 0);
        }
    }

    class Search {
        selectors = {
            input: '.js-info-section-search-input',
            clear: '.js-info-section-search-clear',
            accordionItem: '.js-info-section-accordion-item',
            accordionHeading: '.info-section-accordion-item__control',
            accordionContent: '.js-info-section-accordion-content-inner',
            categoryContainer: '.js-info-section-category-container',
            categoryButton: '.js-info-section-category-button',
            noResults: '.js-info-section-no-results'
        };

        classes = {
            itemHidden: 'is-search-hidden',
            containerEmpty: 'is-search-empty',
            noResultsVisible: 'is-visible'
        };

        constructor(section, categories) {
            this.section = section;
            this.categories = categories;
            this.input = section.querySelector(this.selectors.input);

            if (!this.input) return;

            this.clearButton = section.querySelector(this.selectors.clear);
            this.noResults = section.querySelector(this.selectors.noResults);
            this.containers = [...section.querySelectorAll(this.selectors.categoryContainer)];
            this.items = [...section.querySelectorAll(this.selectors.accordionItem)].map(element => ({
                element,
                container: element.closest(this.selectors.categoryContainer),
                searchText: this.buildSearchText(element)
            }));
            this.lastQuery = null;

            this.bind();
        }

        buildSearchText(element) {
            const heading = element.querySelector(this.selectors.accordionHeading)?.textContent || '';
            const content = element.querySelector(this.selectors.accordionContent)?.textContent || '';
            return `${heading} ${content}`.toLowerCase();
        }

        bind() {
            this.input.addEventListener('input', () => this.handleInput());
            this.clearButton?.addEventListener('click', () => this.clear());
        }

        handleInput() {
            const query = this.input.value.trim().toLowerCase();
            if (this.clearButton) this.clearButton.hidden = query === '';
            if (query === this.lastQuery) return;
            this.lastQuery = query;

            if (query === '') {
                this.exitSearchMode();
                return;
            }

            this.enterSearchMode();
            this.applyFilter(query);
        }

        // search spans every category, so the active filter must be reset to All
        // before applying the search-text filter — otherwise matches in inactive
        // categories would still be hidden by the category filter.
        enterSearchMode() {
            this.categories?.activateAll();
        }

        applyFilter(query) {
            const matchedContainers = new Set();
            this.items.forEach(({ element, container, searchText }) => {
                const matches = searchText.includes(query);
                element.classList.toggle(this.classes.itemHidden, !matches);
                if (matches && container) matchedContainers.add(container);
            });

            this.containers.forEach(container => {
                container.classList.toggle(this.classes.containerEmpty, !matchedContainers.has(container));
            });

            this.noResults?.classList.toggle(this.classes.noResultsVisible, matchedContainers.size === 0);
        }

        exitSearchMode() {
            this.items.forEach(({ element }) => element.classList.remove(this.classes.itemHidden));
            this.containers.forEach(container => container.classList.remove(this.classes.containerEmpty));
            this.noResults?.classList.remove(this.classes.noResultsVisible);
            this.categories?.restoreActiveCategory();
        }

        clear() {
            this.input.value = '';
            this.handleInput();
            this.input.focus();
        }
    }

    class RailOffset {
        selectors = {
            header: '.js-header-wrapper',
            promoBar: '#shopify-section-promo-bar-redesign, .js-announcement-bar, .js-promo-bar'
        };

        constructor(section) {
            this.section = section;
            this.update();
            window.addEventListener('resize', () => this.update());
        }

        // the desktop rail sticks below the pinned header stack. measured
        // here, scoped to this section, because the redesigned promo bar
        // does not populate the global --announcement-bar-height variable
        update() {
            const stack = [this.selectors.header, this.selectors.promoBar]
                .flatMap(selector => [...document.querySelectorAll(selector)])
                .filter(element => {
                    const position = getComputedStyle(element).position;
                    return position === 'sticky' || position === 'fixed';
                });

            const offset = stack.reduce((total, element) => total + element.offsetHeight, 0);

            if (offset > 0) {
                this.section.style.setProperty('--info-rail-top', `${offset}px`);
            }
        }
    }

    if (!customElements.get('section-info')) {
        class SectionInfo extends HTMLElement {
            components = {};

            constructor() {
                super();
            }

            connectedCallback() {
                this.init();
            }

            init() {
                this.components.accordions = new Accordions(this);
                this.components.categories = new Categories(this);
                this.components.search = new Search(this, this.components.categories);
                this.components.railOffset = new RailOffset(this);
                this.querySelector('.js-info-section-chat-button')
                    ?.addEventListener('click', () => window.GolfDaddyChat?.open());
            }
        }

        customElements.define('section-info', SectionInfo);
    }
})();
